import { useEffect } from "react";
import { usePublicSettings } from "@/hooks/useCMSSettings";

export default function PixelManager() {
  const { pixels } = usePublicSettings();

  useEffect(() => {
    if (!pixels) return;

    const cleanupFns: Array<() => void> = [];

    // 1. Facebook Pixel
    if (pixels.fb_pixel?.enabled && pixels.fb_pixel?.id) {
      const fbId = pixels.fb_pixel.id;
      
      // Inject script
      const fbScript = document.createElement("script");
      fbScript.id = "fb-pixel-script";
      fbScript.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${fbId}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(fbScript);

      // Inject noscript image
      const fbNoscript = document.createElement("noscript");
      fbNoscript.id = "fb-pixel-noscript";
      const img = document.createElement("img");
      img.height = 1;
      img.width = 1;
      img.style.display = "none";
      img.src = `https://www.facebook.com/tr?id=${fbId}&ev=PageView&noscript=1`;
      fbNoscript.appendChild(img);
      document.head.appendChild(fbNoscript);

      cleanupFns.push(() => {
        document.getElementById("fb-pixel-script")?.remove();
        document.getElementById("fb-pixel-noscript")?.remove();
        // fbq might pollute window, but usually harmless to leave for session
      });
    }

    // 2. Google Analytics (GA4)
    if (pixels.ga4?.enabled && pixels.ga4?.id) {
      const ga4Id = pixels.ga4.id;

      const gaScriptAsync = document.createElement("script");
      gaScriptAsync.id = "ga4-script-async";
      gaScriptAsync.async = true;
      gaScriptAsync.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
      document.head.appendChild(gaScriptAsync);

      const gaScriptConfig = document.createElement("script");
      gaScriptConfig.id = "ga4-script-config";
      gaScriptConfig.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${ga4Id}');
      `;
      document.head.appendChild(gaScriptConfig);

      cleanupFns.push(() => {
        document.getElementById("ga4-script-async")?.remove();
        document.getElementById("ga4-script-config")?.remove();
      });
    }

    // 3. Google Tag Manager (GTM)
    if (pixels.gtm?.enabled && pixels.gtm?.id) {
      const gtmId = pixels.gtm.id;

      const gtmScript = document.createElement("script");
      gtmScript.id = "gtm-script";
      gtmScript.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');
      `;
      document.head.appendChild(gtmScript);

      cleanupFns.push(() => {
        document.getElementById("gtm-script")?.remove();
      });
    }

    return () => {
      cleanupFns.forEach((fn) => fn());
    };
  }, [pixels]);

  return null;
}
